// Opens the operator drawer (SettingsDrawer) the same ways the Android player
// does, mapped onto the input devices a web kiosk actually has:
//
//   • Touch        → 3-finger hold anywhere reachable (the Android gesture —
//                    see MainActivity.handleMultiFingerGesture for the
//                    cross-platform rationale). Used to be a ONE-finger
//                    long-press, which a customer could — and did — trigger
//                    just by pausing a finger on regular content (an image,
//                    a video, anywhere outside an interactive-menu iframe).
//                    Three simultaneous touch points don't happen from normal
//                    one/two-finger content viewing, so this can't collide
//                    with real customer interaction the way one finger could.
//   • Mouse only   → right-click anywhere (a `contextmenu` event). No press-
//                    and-hold needed, so an operator with just a mouse has a
//                    single deliberate gesture — a mouse can't produce 3
//                    simultaneous pointers anyway.
//   • Remote control → the remote's MENU / OPTIONS button. On TV browsers /
//                    WebViews that key surfaces as either a `contextmenu`
//                    event or a `ContextMenu` KeyboardEvent — we handle both.
//   • Keyboard     → `m` (the explicit stand-in for the Android MENU key).
//
// Android watches its gesture at dispatchTouchEvent so it fires even over the
// content WebView (a native child view, same window). The browser can't see
// pointer events that land inside a cross-frame content <iframe>
// (HtmlFrameDisplay's interactive menus) — that's a hard browsing-context
// boundary, not something fixable here — but a hold on any chrome / state
// screen / letterbox area, or over non-iframe content (images/video/etc.,
// which ARE normal same-document elements), still reaches this window
// listener. OperatorHotspot's small corner button is the only thing that can
// reach even INSIDE an interactive-menu iframe, since it's a real DOM element
// physically stacked above it in that one region.
//
// Hold = REQUIRED_POINTERS pointers all down together, none moving past
// MOVE_TOLERANCE, for HOLD_MS. The hold path never preventDefaults, so taps/
// scrolls still reach interactive content and a normal left-click is
// unaffected. `contextmenu` IS suppressed so the browser's own right-click
// menu never shows on the kiosk.

import { useEffect, useState } from 'react'

const HOLD_MS = 700
const REQUIRED_POINTERS = 3
const MOVE_TOLERANCE = 12  // px — any tracked finger drifting past this cancels the hold

export function useOperatorMenu(): { open: boolean; setOpen: (v: boolean) => void } {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let timer: number | null = null
    // pointerId -> where THAT finger started, so a single wandering finger
    // (not necessarily the one that completed the trio) still cancels.
    const starts = new Map<number, { x: number; y: number }>()

    const clearTimer = () => {
      if (timer !== null) { window.clearTimeout(timer); timer = null }
    }
    const cancelHold = () => {
      clearTimer()
      starts.clear()
    }

    const onPointerDown = (e: PointerEvent) => {
      // Ignore while already open so a press on the backdrop closes
      // (handled by the drawer) instead of re-arming.
      if (open) return
      starts.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (starts.size >= REQUIRED_POINTERS && timer === null) {
        timer = window.setTimeout(() => { timer = null; setOpen(true); starts.clear() }, HOLD_MS)
      }
    }
    const onPointerMove = (e: PointerEvent) => {
      const start = starts.get(e.pointerId)
      if (!start) return
      if (Math.abs(e.clientX - start.x) > MOVE_TOLERANCE ||
          Math.abs(e.clientY - start.y) > MOVE_TOLERANCE) {
        cancelHold()
      }
    }
    const onPointerUp = (e: PointerEvent) => {
      starts.delete(e.pointerId)
      if (starts.size < REQUIRED_POINTERS) clearTimer()
    }
    // Right-click (mouse) + many remote MENU buttons arrive as `contextmenu`.
    // Open (not toggle) and always suppress the browser's native menu. Opening
    // is idempotent — harmless if a touch hold and a synthesized contextmenu
    // both land, though a 3-finger hold isn't a gesture browsers map to
    // contextmenu synthesis the way a single-finger long-press used to be.
    // Closing is via the drawer's backdrop / close button / Escape.
    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault()
      cancelHold()
      setOpen(true)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      // Ignore when typing into a field (e.g. a pairing-code input) so we
      // don't hijack text entry.
      const el = e.target as HTMLElement | null
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
      if (typing) return
      // `m` = explicit MENU stand-in; `ContextMenu` = the remote's MENU /
      // OPTIONS button when it surfaces as a key rather than a contextmenu.
      if (e.key === 'm' || e.key === 'M' || e.key === 'ContextMenu') {
        e.preventDefault(); setOpen(v => !v)
      } else if (e.key === 'Escape' && open) { setOpen(false) }
    }

    window.addEventListener('pointerdown', onPointerDown, { passive: true })
    window.addEventListener('pointermove', onPointerMove, { passive: true })
    window.addEventListener('pointerup', onPointerUp, { passive: true })
    window.addEventListener('pointercancel', onPointerUp, { passive: true })
    window.addEventListener('contextmenu', onContextMenu)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      cancelHold()
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      window.removeEventListener('contextmenu', onContextMenu)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return { open, setOpen }
}
